/**
 * 회의록 내려받기 버튼 — §11.4.
 *
 * **참가자 누구나 자기 화면에서 받는다.** 호스트만 회의록을 가질 수 있으면,
 * 통역을 받은 쪽은 회의가 끝나는 순간 자기가 무슨 말을 들었는지 확인할 방법이
 * 없다. 호스트·뷰어·익명 패스코드 뷰어가 같은 버튼을 쓰고, **범위는 서버가**
 * 각자의 키로 정한다(server/export.py — 팬아웃과 같은 vis 필터).
 *
 * 화면이 하지 않는 일:
 *  - 내용 필터링. 받아 놓고 숨기는 것은 격리가 아니다.
 *  - 형식 강요. 기본은 `.txt`(받아서 바로 여는 형식)이되, 회의록을 다른
 *    도구로 넘길 사람을 위해 `.md`/`.json` 도 같은 자리에서 고를 수 있다.
 *
 * 기록 미보관 방의 경계는 **누르기 전에** 말한다. 받고 나서 "사실 일부였다"
 * 를 알게 되는 것은 늦다 — 회의록은 없는 줄 알면 다른 수단을 쓸 수 있었다.
 */

import { useCallback, useState } from 'react'
import { Button } from '@cupia/ui/button'
import { Download, FileDown, Loader2 } from 'lucide-react'
import {
  EXPORT_FORMATS,
  FORMAT_LABELS,
  PARTIAL_NOTE,
  downloadTranscript,
  type ExportFormat,
} from '../lib/export'
import { ApiError } from '../lib/api'

export interface TranscriptDownloadProps {
  /** 불투명 방 ID(§10.0) */
  room: string
  /** host_key 또는 view_key — **서버가 이 키로 범위를 정한다** */
  authKey: string
  /**
   * 이 방이 기록을 보관하는지(§11.3 — 회의 **도중** 바뀔 수 있다).
   * 화면은 이 값으로 경계 문구만 그린다. 실제 판정은 서버가 요청 시점에
   * 다시 한다 — 여기 값이 오래됐어도 파일 자체는 정확하다.
   */
  recorded?: boolean
  /** 방이 사라졌거나 캡처 전이라 누를 수 없는 상태 */
  disabled?: boolean
  /** 결과 알림 — 호스트/뷰어 화면의 토스트에 연결한다 */
  onNotify?: (message: string) => void
  /** 버튼 크기(헤더/툴바 문맥에 맞춘다) — DS 의 size 토큰을 그대로 쓴다 */
  size?: 'xs' | 'sm' | 'md' | 'lg'
  /** 버튼 변형 */
  variant?: 'outline' | 'ghost' | 'secondary'
}

export function TranscriptDownload({
  room,
  authKey,
  recorded = false,
  disabled = false,
  onNotify,
  size = 'sm',
  variant = 'outline',
}: TranscriptDownloadProps): React.ReactElement {
  const [busy, setBusy] = useState<ExportFormat | null>(null)
  const [open, setOpen] = useState(false)

  const run = useCallback(
    async (fmt: ExportFormat) => {
      if (busy) return
      setBusy(fmt)
      try {
        const file = await downloadTranscript(room, authKey, fmt)
        setOpen(false)
        // 몇 건을 받았는지 말한다 — 0건이면 "받았는데 비었다" 를 알아야
        // 사용자가 실패로 오해하지 않는다.
        const scope = file.partial ? ` · ${PARTIAL_NOTE}` : ''
        onNotify?.(`회의록을 저장했습니다(${file.count}건).${scope}`)
      } catch (e) {
        const msg =
          e instanceof ApiError ? e.message : '회의록을 받지 못했습니다.'
        onNotify?.(msg)
      } finally {
        setBusy(null)
      }
    },
    [busy, room, authKey, onNotify],
  )

  // 자격이 없으면 버튼 자체를 그리지 않는다 — 눌러도 안 되는 버튼을 두지
  // 않는다는 이 앱의 규칙(§14.2)을 따른다.
  if (!room || !authKey) return <></>

  return (
    <div className="relative inline-flex" data-testid="transcript-download">
      <Button
        variant={variant}
        size={size}
        disabled={disabled || busy !== null}
        onClick={() => {
          // 한 번 누르면 기본 형식(.txt)으로 바로 받는다 — 가장 흔한 요구를
          // 클릭 하나로 끝낸다. 다른 형식은 옆의 화살표에서 고른다.
          void run('txt')
        }}
        title="이 화면에서 볼 수 있는 회의록을 저장합니다"
        data-testid="transcript-download-default"
      >
        {busy ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Download className="h-4 w-4" />
        )}
        회의록 저장
      </Button>
      <Button
        variant={variant}
        size={size}
        disabled={disabled || busy !== null}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="회의록 형식 선택"
        onClick={() => setOpen((v) => !v)}
        data-testid="transcript-download-more"
      >
        <FileDown className="h-4 w-4" />
      </Button>

      {open && (
        <div
          role="menu"
          data-testid="transcript-download-menu"
          className="absolute end-0 top-full z-50 mt-1 min-w-56 rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-popover)] p-1 shadow-lg"
        >
          {EXPORT_FORMATS.map((fmt) => (
            <button
              key={fmt}
              type="button"
              role="menuitem"
              disabled={busy !== null}
              onClick={() => void run(fmt)}
              data-testid={`transcript-format-${fmt}`}
              className="flex w-full items-center gap-2 rounded-[var(--radius-md)] px-2 py-1.5 text-start text-(length:--text-sm-size) hover:bg-[var(--color-accent)] disabled:opacity-50"
            >
              <Download className="h-3.5 w-3.5" />
              {FORMAT_LABELS[fmt]}
            </button>
          ))}
          {/* 경계는 **누르기 전에** 말한다(§11 무저장 기본). */}
          {!recorded && (
            <p
              data-testid="transcript-partial-note"
              className="border-t border-[var(--color-border)] px-2 py-1.5 text-(length:--text-xs-size) text-[var(--color-muted-foreground)]"
            >
              {PARTIAL_NOTE}
            </p>
          )}
        </div>
      )}
    </div>
  )
}
